import React, { useState, useEffect } from 'react';
import { Share2, Copy, Check, X, MessageCircle, Mail, Linkedin, Twitter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/services/api';

const CONSENT_STORAGE_KEY = 'harmonix_gdpr_consent';

/**
 * ShareToEarnWidget — floating action button on /refer-and-earn that
 * makes the "share your link" step as low-friction as physically
 * possible.
 *
 * Behaviour:
 *   • Mobile → tries the native ``navigator.share()`` sheet first
 *     (that's where 70%+ of affiliate clicks come from — one tap into
 *     WhatsApp/Messages).
 *   • Desktop / no share API → opens a small popover with copy-link +
 *     4 platform-specific share URLs (WhatsApp / Email / LinkedIn / X).
 *
 * The link it shares is a generic marketing link (utm_source=share-widget)
 * — a signed-in user gets their real referral link inside the dashboard.
 * The purpose of this widget is to reduce friction for *first-time*
 * visitors evaluating whether to sign up as a referrer at all.
 */
const SHARE_URL = 'https://harmonix.info/refer-and-earn?utm_source=share-widget&utm_campaign=perpetual';
// Share title/body are computed at runtime so the "up to X%" copy
// always matches the actual product config fetched from
// /api/affiliate/public-config. Falls back to a rate-free string if
// the endpoint is momentarily unreachable.
const buildShareTitle = (topRate) =>
  topRate
    ? `Earn up to ${topRate}% recurring commission — forever — with Harmonix`
    : 'Earn recurring commission — forever — with Harmonix';
const SHARE_BODY = 'Just found a SaaS affiliate program that pays perpetual commissions (no 12-month cliff): ';

export const ShareToEarnWidget = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const [topRate, setTopRate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/affiliate/public-config')
      .then((r) => { if (!cancelled && r?.data?.top_commission_rate) setTopRate(r.data.top_commission_rate); })
      .catch(() => { /* rate stays null → shareTitle falls back */ });
    return () => { cancelled = true; };
  }, []);

  const SHARE_TITLE = buildShareTitle(topRate);
  // Track whether the GDPR consent banner is currently occupying the
  // bottom of the viewport — if so we lift the FAB above it (bottom-24)
  // so users can actually reach it. Otherwise it sits at the normal
  // bottom-6 position. Re-check when the tab regains focus (user may
  // have accepted the banner in another tab).
  const [bannerVisible, setBannerVisible] = useState(false);
  useEffect(() => {
    const check = () => {
      try {
        setBannerVisible(!localStorage.getItem(CONSENT_STORAGE_KEY));
      } catch {
        setBannerVisible(false);
      }
    };
    check();
    window.addEventListener('focus', check);
    window.addEventListener('storage', check);
    return () => {
      window.removeEventListener('focus', check);
      window.removeEventListener('storage', check);
    };
  }, []);

  useEffect(() => {
    // Feature-detect once on mount. Native share is only exposed on
    // secure contexts (https or localhost) — on http staging it's absent.
    setCanNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const handleFabClick = async () => {
    if (canNativeShare) {
      try {
        await navigator.share({ title: SHARE_TITLE, text: SHARE_BODY, url: SHARE_URL });
        return;
      } catch (err) {
        // AbortError = user dismissed the sheet — no toast, no popover.
        if (err && err.name === 'AbortError') return;
        // Any other error (permission, unsupported field) — fall through
        // to the desktop popover as a graceful degrade.
      }
    }
    setOpen((o) => !o);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — long-press the link to copy manually.');
    }
  };

  const platforms = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(SHARE_BODY + SHARE_URL)}`,
      tone: 'text-emerald-600 hover:bg-emerald-50',
    },
    {
      key: 'email',
      label: 'Email',
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodeURIComponent(SHARE_BODY + '\n\n' + SHARE_URL)}`,
      tone: 'text-slate-600 hover:bg-slate-100',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}`,
      tone: 'text-sky-700 hover:bg-sky-50',
    },
    {
      key: 'twitter',
      label: 'X / Twitter',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(SHARE_TITLE)}`,
      tone: 'text-slate-800 hover:bg-slate-100',
    },
  ];

  return (
    <div
      className={`fixed right-6 z-[60] transition-[bottom] duration-300 ${bannerVisible ? 'bottom-40 sm:bottom-32' : 'bottom-6'}`}
      data-testid="share-to-earn-widget"
    >
      {/* Popover — desktop / no-native-share fallback */}
      {open && (
        <div
          className="absolute bottom-16 right-0 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200"
          data-testid="share-to-earn-popover"
        >
          <div className="p-4 bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold">Share Harmonix</p>
                <p className="text-[11px] text-emerald-100">Perpetual 30% commission</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                data-testid="share-to-earn-close"
                aria-label="Close share menu"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="p-3 space-y-1">
            {platforms.map((p) => (
              <a
                key={p.key}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${p.tone}`}
                data-testid={`share-to-earn-${p.key}`}
                onClick={() => setOpen(false)}
              >
                <p.icon className="w-4 h-4" />
                {p.label}
              </a>
            ))}
            <button
              type="button"
              onClick={copyLink}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="share-to-earn-copy"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
            <p className="text-[10px] text-slate-500 leading-relaxed">
              Not a Harmonix affiliate yet?{' '}
              <a href="/login?signup=true&utm_source=share-widget" className="text-emerald-700 font-semibold underline">
                Sign up in 60 seconds
              </a>{' '}
              to get your own link and start earning.
            </p>
          </div>
        </div>
      )}

      {/* FAB — always visible on the page */}
      <Button
        onClick={handleFabClick}
        className="h-14 pl-4 pr-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02]"
        data-testid="share-to-earn-fab"
        aria-label="Share Harmonix Refer & Earn"
      >
        <Share2 className="w-5 h-5 mr-2" />
        <span className="font-semibold text-sm">Share &amp; earn</span>
      </Button>
    </div>
  );
};

export default ShareToEarnWidget;
